import React, { useEffect, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Code, Text, VStack, HStack, Spinner, Alert, AlertIcon,
  Box, Badge, Collapse, Divider,
} from "@chakra-ui/react";
import {
  previewRlsFix, applyRlsFix, previewRlsFixBatch, applyRlsFixBatch,
} from "../../services/rlsAudit";

const SECTIONS = [
  { key: "removing", title: "Removing", scheme: "red",
    blurb: "These grant every authenticated user full access, so they cancel out the scoped policies." },
  { key: "adding", title: "Adding", scheme: "green",
    blurb: "The policies this table will have afterwards." },
  { key: "keeping", title: "Keeping", scheme: "gray",
    blurb: "Left untouched." },
];

function PolicyList({ section, policies }) {
  if (!policies || policies.length === 0) return null;
  const { scheme, title, blurb } = section;
  return (
    <Box
      borderLeftWidth="4px"
      borderLeftColor={`${scheme}.400`}
      bg={`${scheme}.50`}
      borderRadius="md"
      px={4}
      py={3}
    >
      <HStack mb={1}>
        <Text fontSize="lg" fontWeight="bold" color={`${scheme}.700`}>
          {title}
        </Text>
        <Badge colorScheme={scheme} fontSize="sm">{policies.length}</Badge>
      </HStack>
      <Text fontSize="sm" color="gray.600" mb={3}>{blurb}</Text>
      <VStack align="stretch" spacing={2}>
        {policies.map((p) => (
          <Box key={`${p.name}-${p.cmd}`}>
            <HStack spacing={3} align="baseline" flexWrap="wrap">
              <Text fontSize="md" fontWeight="semibold">{p.name}</Text>
              <Badge colorScheme={scheme} variant="solid">{p.cmd}</Badge>
              <Text fontSize="sm" color="gray.600">
                {(p.roles || []).join(", ")}
              </Text>
            </HStack>
            {p.note && (
              <Text fontSize="sm" color={`${scheme}.700`} mt={0.5}>{p.note}</Text>
            )}
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

function SqlBlock({ title, statements }) {
  return (
    <Box mb={3}>
      <Text fontWeight="bold" fontSize="sm" mb={1}>{title}</Text>
      <Box maxH="200px" overflowY="auto" overflowX="auto" bg="gray.50" p={2} borderRadius="md">
        {statements.map((s, i) => (
          <Code key={i} display="block" whiteSpace="pre" fontSize="xs" bg="transparent">
            {s};
          </Code>
        ))}
      </Box>
    </Box>
  );
}

export default function RlsFixDialog({ target, onClose, onApplied }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [errors, setErrors] = useState([]);
  const [showSql, setShowSql] = useState(false);

  useEffect(() => {
    if (!target) { setPreview(null); setErrors([]); setShowSql(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    const request = target.batch
      ? previewRlsFixBatch(target.schema)
      : previewRlsFix(target.schema, target.table);
    request
      .then((p) => { if (!cancelled) setPreview(p); })
      .catch((err) => { if (!cancelled) setErrors([err.message]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [target]);

  if (!target) return null;

  const onApply = async () => {
    setApplying(true);
    setErrors([]);
    try {
      const res = target.batch
        ? await applyRlsFixBatch(target.schema)
        : await applyRlsFix(target.schema, target.table);
      const errs = target.batch
        ? (res.failed || []).flatMap((f) =>
            f.errors.map((e) => `${f.schema}.${f.table}: ${e}`))
        : (res.errors || []);
      if (errs.length) {
        setErrors(errs);
        return;   // do NOT report success or close
      }
      onApplied();
      onClose();
    } catch (err) {
      setErrors([err.message]);
    } finally {
      setApplying(false);
    }
  };

  const count = preview?.targets?.length ?? 0;
  const changes = preview?.changes;

  return (
    <Modal isOpen onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {target.batch
            ? `Fix All — ${target.schema || "every schema"}`
            : `Fix RLS — ${target.schema}.${target.table}`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading && <Spinner />}
          {errors.length > 0 && (
            <Alert status="error" mb={3} flexDirection="column" alignItems="flex-start">
              <Box display="flex"><AlertIcon />Some statements failed</Box>
              {errors.map((e, i) => (
                <Text key={i} fontSize="sm" mt={1}>{e}</Text>
              ))}
            </Alert>
          )}

          {preview && (
            <VStack align="stretch" spacing={4}>
              <Alert status="warning" fontSize="sm">
                <AlertIcon />
                This runs against production. A policy that is too strict returns zero
                rows rather than an error — keep the rollback below.
              </Alert>

              {target.batch && (
                <Text fontSize="lg" fontWeight="bold">
                  {count} table{count === 1 ? "" : "s"} will change
                </Text>
              )}

              {changes?.enable_rls && (
                <Box borderLeftWidth="4px" borderLeftColor="blue.400" bg="blue.50"
                     borderRadius="md" px={4} py={3}>
                  <Text fontSize="lg" fontWeight="bold" color="blue.700">
                    Turning on Row Level Security
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    It is currently off, so every authenticated user can read every row.
                  </Text>
                </Box>
              )}

              {changes && SECTIONS.map((section) => (
                <PolicyList
                  key={section.key}
                  section={section}
                  policies={changes[section.key]}
                />
              ))}

              <Divider />
              <Box>
                <Button size="sm" variant="link" onClick={() => setShowSql((v) => !v)}>
                  {showSql ? "Hide SQL" : "Show SQL"}
                </Button>
                {/* unmountOnExit: Collapse keeps children mounted otherwise, so a
                    large SQL block would sit in the DOM (and in test queries)
                    even while visually hidden. */}
                <Collapse in={showSql} animateOpacity unmountOnExit>
                  <Box pt={3}>
                    <SqlBlock title="Will run" statements={preview.forward} />
                    <SqlBlock title="Rollback (save this)" statements={preview.rollback} />
                  </Box>
                </Collapse>
              </Box>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button
            colorScheme="orange"
            onClick={onApply}
            isLoading={applying}
            isDisabled={!preview || preview.forward.length === 0}
          >
            Apply
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
